/**
 * Parses vCard (.vcf) files into structured contact data.
 * Supports vCard 2.1, 3.0, and 4.0 formats.
 */

export interface SocialProfile {
  type: string; // messenger, whatsapp, instagram, twitter, linkedin, other
  username: string;
}

export interface ParsedContact {
  name: string;
  phones: string[];
  emails: string[];
  photoDataUrl?: string; // base64 data URL from PHOTO property
  birthday?: string; // YYYY-MM-DD from BDAY property
  socialProfiles: SocialProfile[];
}

export function parseVCards(vcfText: string): ParsedContact[] {
  const contacts: ParsedContact[] = [];
  // Split into individual vCards
  const cards = vcfText.split(/(?=BEGIN:VCARD)/i).filter((c) => c.trim());

  for (const card of cards) {
    const lines = unfoldLines(card);
    let name = '';
    const phones: string[] = [];
    const emails: string[] = [];
    let photoDataUrl: string | undefined;
    let birthday: string | undefined;
    const socialProfiles: SocialProfile[] = [];

    for (const line of lines) {
      const upper = line.toUpperCase();

      // Full name (FN:John Doe)
      if (upper.startsWith('FN')) {
        name = extractValue(line);
      }
      // Structured name fallback (N:Doe;John;;;)
      else if (upper.startsWith('N:') || upper.startsWith('N;')) {
        if (!name) {
          const parts = extractValue(line).split(';');
          const lastName = parts[0] || '';
          const firstName = parts[1] || '';
          name = `${firstName} ${lastName}`.trim();
        }
      }
      // Phone (TEL;TYPE=CELL:+1234567890)
      else if (upper.startsWith('TEL')) {
        const val = extractValue(line).trim();
        if (val) phones.push(val);
      }
      // Email (EMAIL;TYPE=HOME:user@example.com)
      else if (upper.startsWith('EMAIL')) {
        const val = extractValue(line).trim();
        if (val) emails.push(val);
      }
      // Photo — vCard 2.1/3.0: PHOTO;ENCODING=b;TYPE=JPEG:<base64>
      //         vCard 4.0: PHOTO:data:image/jpeg;base64,<base64>
      else if (upper.startsWith('PHOTO')) {
        const raw = extractValue(line);
        if (raw.startsWith('data:')) {
          // vCard 4.0 — already a data URL
          photoDataUrl = raw;
        } else if (raw.length > 100) {
          // vCard 2.1/3.0 — raw base64 data, detect type from params
          const mimeType = extractPhotoMimeType(line);
          photoDataUrl = `data:${mimeType};base64,${raw}`;
        }
      }
      // Birthday — BDAY:19900315 or BDAY:1990-03-15 or BDAY;VALUE=date:1990-03-15
      else if (upper.startsWith('BDAY')) {
        const val = extractValue(line).trim();
        birthday = parseBirthday(val);
      }
      // Social profiles — iOS: X-SOCIALPROFILE;type=Messenger;x-user=john:x-apple:john
      // Also handles: X-SOCIALPROFILE;type=Twitter;x-user=john:https://twitter.com/john
      else if (upper.startsWith('X-SOCIALPROFILE')) {
        const profile = extractSocialProfile(line);
        if (profile) socialProfiles.push(profile);
      }
    }

    if (name) {
      contacts.push({ name, phones, emails, photoDataUrl, birthday, socialProfiles });
    }
  }

  return contacts;
}

/**
 * iOS social profile type → Nudge communication channel type mapping.
 * iOS uses: Facebook, Messenger, Twitter, LinkedIn, Instagram, Flickr, Myspace, SinaWeibo, etc.
 * WhatsApp contacts are typically stored as TEL with label, not X-SOCIALPROFILE.
 */
const SOCIAL_TYPE_MAP: Record<string, string> = {
  messenger: 'messenger',
  facebook: 'messenger', // Facebook profile → treat as Messenger
  twitter: 'twitter',
  instagram: 'instagram',
  linkedin: 'linkedin',
  whatsapp: 'whatsapp',
};

/**
 * Extract social profile from X-SOCIALPROFILE line.
 * iOS format: X-SOCIALPROFILE;type=Messenger;x-user=dhalashish:x-apple:dhalashish
 * Standard:   X-SOCIALPROFILE;type=Twitter;x-user=jack:https://twitter.com/jack
 */
function extractSocialProfile(line: string): SocialProfile | undefined {
  // Extract type parameter (case-insensitive)
  const typeMatch = line.match(/[;:]type=([^;:]+)/i);
  if (!typeMatch) return undefined;

  const rawType = typeMatch[1].toLowerCase();
  const mappedType = SOCIAL_TYPE_MAP[rawType] || 'other';

  // Extract x-user parameter (the username) — most reliable
  const userMatch = line.match(/x-user=([^;:]+)/i);
  if (userMatch) {
    return { type: mappedType, username: userMatch[1].trim() };
  }

  // Fallback: extract from the value after the colon
  // e.g., :https://twitter.com/jack → "jack"
  // e.g., :x-apple:dhalashish → "dhalashish"
  const value = extractValue(line).trim();
  if (!value) return undefined;

  // Try to extract username from URL
  const urlMatch = value.match(/(?:twitter|instagram|x)\.com\/([^/?]+)/i);
  if (urlMatch) {
    return { type: mappedType, username: urlMatch[1] };
  }

  // Try x-apple:username pattern (iOS internal)
  const appleMatch = value.match(/x-apple:(.+)/i);
  if (appleMatch) {
    return { type: mappedType, username: appleMatch[1].trim() };
  }

  // Last resort: use the value itself
  if (value.length > 0 && value.length < 100) {
    return { type: mappedType, username: value };
  }

  return undefined;
}

/**
 * Extract MIME type from PHOTO property parameters.
 * e.g., PHOTO;ENCODING=b;TYPE=JPEG → image/jpeg
 */
function extractPhotoMimeType(line: string): string {
  const upper = line.toUpperCase();
  if (upper.includes('TYPE=PNG')) return 'image/png';
  if (upper.includes('TYPE=GIF')) return 'image/gif';
  return 'image/jpeg'; // default
}

/**
 * Parse birthday value into YYYY-MM-DD format.
 * Handles: 19900315, 1990-03-15, --0315 (year unknown)
 */
function parseBirthday(val: string): string | undefined {
  // ISO format: 1990-03-15
  const isoMatch = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;

  // Compact format: 19900315
  const compactMatch = val.match(/^(\d{4})(\d{2})(\d{2})/);
  if (compactMatch) return `${compactMatch[1]}-${compactMatch[2]}-${compactMatch[3]}`;

  return undefined;
}

/**
 * vCard lines can be folded (continuation lines start with space/tab).
 * Unfold them back into single lines.
 */
function unfoldLines(text: string): string[] {
  return text
    .replace(/\r\n[ \t]/g, '')
    .replace(/\r/g, '')
    .split('\n');
}

/**
 * Extract the value from a vCard property line.
 * Handles properties with parameters (e.g., TEL;TYPE=CELL:+1234)
 */
function extractValue(line: string): string {
  const colonIdx = line.indexOf(':');
  if (colonIdx === -1) return '';
  return line.substring(colonIdx + 1).trim();
}
